import { useRef } from 'react'
import type { CompanionManager } from '@edge/edge-ai'
import { useDebug } from '@/ett-debug-tools'
import { AI_DEBUG_CHANNEL, companionRoundToMeta, type LlmSessionTotals } from './llm-call-meta'

/**
 * Bridges `CompanionManager.onDebug` into the dev debug sidebar. Every model
 * round is mapped into the generic `LlmCallMeta` envelope and emitted on the
 * `ai` channel, keyed by the manager's conversation id so a whole session
 * groups under one tab.
 *
 * Accumulates running conversation totals in a ref so each entry can show a
 * session rollup (the per-entry view can't see sibling entries). In prod
 * `useDebug` is a noop, so `manager.onDebug` is never attached and the whole
 * bridge is inert (and tree-shaken).
 */
export function useCompanionAiDebug(manager: CompanionManager): void {
  const totals = useRef<LlmSessionTotals>(freshTotals())
  useDebug(
    AI_DEBUG_CHANNEL,
    manager.conversationId,
    (emit) => {
      totals.current = freshTotals()
      return manager.onDebug((round) => {
        const t = totals.current
        totals.current = {
          rounds: t.rounds + 1,
          modelMs: t.modelMs + round.totalMs,
          toolMs: t.toolMs + (round.toolMs ?? 0),
          tokensIn: (t.tokensIn ?? 0) + (round.tokensIn ?? 0),
          tokensOut: (t.tokensOut ?? 0) + (round.tokensOut ?? 0),
          costUsd: (t.costUsd ?? 0) + (round.costUsd ?? 0),
        }
        emit(companionRoundToMeta(round, { ...totals.current }))
      })
    },
    [manager],
  )
}

function freshTotals(): LlmSessionTotals {
  return { rounds: 0, modelMs: 0, toolMs: 0, tokensIn: 0, tokensOut: 0, costUsd: 0 }
}
